import { initClient, tsRestFetchApi, type ApiFetcherArgs } from '@ts-rest/core';
import { contract } from '@aimmo/contracts';
import { ResourceRequestError, assertOk } from '@aimmo/ui';
import { endpointOf, errorKindOf, trackEvent } from './stats.js';

/**
 * One error type for the whole SPA.
 *
 * The resource kit reads `fields` off this to put server messages on the right
 * inputs; a second, parallel error class would look identical and silently lose
 * that mapping.
 */
export { ResourceRequestError };

/**
 * The kit's own success check, re-exported so every call in the SPA throws the
 * same error a kit form maps onto its fields — one helper, not two copies.
 */
export { assertOk };

let csrfToken: string | null = null;

/** Set from the session response; echoed on every state-changing request. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/**
 * Read back for the one request that cannot go through the ts-rest client:
 * the assistant's SSE endpoint answers with a stream, which ts-rest cannot
 * type, so it is a plain `fetch` (`features/assistant/useAssistantStream.ts`)
 * that still has to carry the same header every other unsafe request does.
 */
export function getCsrfToken(): string | null {
  return csrfToken;
}

/**
 * What the usage statistics learn of a failed request (M17, section E): that
 * `POST /properties` answered 500, never what was sent or who sent it. The
 * endpoint is the contract's own path template — `/properties/:id`, not the
 * URL requested — and the area its first segment.
 */
function reportRequestError(args: ApiFetcherArgs, status: number): void {
  const kind = errorKindOf(status);
  if (!kind) return;
  const { endpoint, area } = endpointOf(args.method, args.route.path);
  trackEvent(window, { name: 'error-shown', data: { kind, status, endpoint, area } });
}

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export const api = initClient(contract, {
  // Same origin: nginx serves the SPA and proxies /api (ADR-0016).
  baseUrl: '',
  baseHeaders: {},
  api: async (args: ApiFetcherArgs) => {
    const headers: Record<string, string> = { ...args.headers };
    if (csrfToken && UNSAFE_METHODS.has(args.method.toUpperCase())) {
      headers['x-csrf-token'] = csrfToken;
    }

    let result: Awaited<ReturnType<typeof tsRestFetchApi>>;
    try {
      result = await tsRestFetchApi({
        ...args,
        headers,
        // The session cookie is the credential; nothing is stored in JS.
        credentials: 'same-origin',
      });
    } catch (error) {
      reportRequestError(args, 0);
      throw error;
    }
    reportRequestError(args, result.status);

    // A dead session is not an application error — it is a login.
    if (result.status === 401 && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/api/auth/login';
    }

    return result;
  },
});
